import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Upload, FileText, X, Loader2 } from "lucide-react";
import { VendorSelectWithCreate } from "@/components/controller/VendorSelectWithCreate";
import { AccountSelect } from "@/components/controller/AccountSelect";
import { useCreateBill, type NewBillLineInput } from "@/hooks/useBills";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

interface LineDraft extends NewBillLineInput {
  _key: string;
}

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

const newLine = (): LineDraft => ({
  _key: crypto.randomUUID(),
  description: "",
  quantity: 1,
  unit_price: 0,
  line_total: 0,
  cost_code_id: null,
  account_id: null,
});

const suggestBillNumber = () =>
  `MANUAL-${new Date().toISOString().slice(0, 10).replace(/-/g, "")}-${String(Math.floor(Math.random() * 900) + 100)}`;

export const NewBillDialog = ({ open, onOpenChange, onSuccess }: Props) => {
  const createBill = useCreateBill();

  const [vendorId, setVendorId] = useState("");
  const [projectId, setProjectId] = useState<string>("");
  const [billNumber, setBillNumber] = useState("");
  const [billDate, setBillDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState("");
  const [terms, setTerms] = useState<string>("net_30");
  const [description, setDescription] = useState("");
  const [lines, setLines] = useState<LineDraft[]>([newLine()]);
  const [files, setFiles] = useState<File[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setVendorId("");
      setProjectId("");
      setBillNumber(suggestBillNumber());
      setBillDate(new Date().toISOString().slice(0, 10));
      setDueDate("");
      setTerms("net_30");
      setDescription("");
      setLines([newLine()]);
      setFiles([]);
    }
  }, [open]);

  // Auto-compute due date from terms
  useEffect(() => {
    if (!billDate) return;
    const daysMap: Record<string, number> = {
      due_on_receipt: 0, net_15: 15, net_30: 30, net_45: 45, net_60: 60,
    };
    const d = daysMap[terms];
    if (d == null) return;
    const dt = new Date(billDate + "T00:00:00");
    dt.setDate(dt.getDate() + d);
    setDueDate(dt.toISOString().slice(0, 10));
  }, [billDate, terms]);

  // Fetch projects
  const { data: projects } = useQuery({
    queryKey: ["projects_for_new_bill"],
    queryFn: async () => {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) return [];
      const { data } = await supabase
        .from("projects")
        .select("id, name, project_number, status")
        .eq("company_id", companyId)
        .in("status", ["active", "on_hold"])
        .order("name");
      return data ?? [];
    },
    enabled: open,
  });

  // Fetch cost codes when a project is selected
  const { data: costCodes } = useQuery({
    queryKey: ["cost_codes_for_new_bill"],
    queryFn: async () => {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) return [];
      const { data } = await supabase
        .from("cost_codes")
        .select("id, code, name")
        .eq("company_id", companyId)
        .order("code");
      return data ?? [];
    },
    enabled: open && !!projectId,
  });

  const hasProject = !!projectId;

  const subtotal = useMemo(
    () => lines.reduce((s, l) => s + (Number(l.line_total) || 0), 0),
    [lines]
  );

  const updateLine = (key: string, patch: Partial<LineDraft>) => {
    setLines((prev) =>
      prev.map((l) => {
        if (l._key !== key) return l;
        const merged = { ...l, ...patch };
        if ("quantity" in patch || "unit_price" in patch) {
          merged.line_total = Number(merged.quantity || 0) * Number(merged.unit_price || 0);
        }
        return merged;
      })
    );
  };

  const removeLine = (key: string) =>
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((l) => l._key !== key)));

  const addLine = () => setLines((prev) => [...prev, newLine()]);

  const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    if (picked.length) setFiles((prev) => [...prev, ...picked]);
    if (fileRef.current) fileRef.current.value = "";
  };

  // Validation
  const validationError = useMemo(() => {
    if (!vendorId) return "Vendor is required";
    if (!billNumber.trim()) return "Bill number is required";
    if (!billDate) return "Bill date is required";
    if (!lines.length) return "Add at least one line item";
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      if (!l.description.trim()) return `Line ${i + 1}: description is required`;
      if (!(Number(l.line_total) > 0)) return `Line ${i + 1}: amount must be greater than zero`;
      if (hasProject) {
        if (!l.cost_code_id && !l.account_id) {
          return `Line ${i + 1}: pick a cost code or account`;
        }
      } else {
        if (!l.account_id) return `Line ${i + 1}: pick an account`;
      }
    }
    return null;
  }, [vendorId, billNumber, billDate, lines, hasProject]);

  const canSubmit = !validationError && !createBill.isPending;

  const handleSubmit = async () => {
    if (validationError) {
      toast.error(validationError);
      return;
    }
    try {
      await createBill.mutateAsync({
        vendor_id: vendorId,
        project_id: hasProject ? projectId : null,
        bill_number: billNumber.trim(),
        bill_date: billDate,
        due_date: dueDate || null,
        payment_terms: terms || null,
        description: description.trim() || null,
        lines: lines.map(({ _key, ...rest }) => rest),
        attachments: files,
      });
      toast.success(`Bill ${billNumber} created and sent for approval`);
      onOpenChange(false);
      onSuccess?.();
    } catch (e: any) {
      toast.error(e.message ?? "Failed to create bill");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Bill</DialogTitle>
          <DialogDescription>
            Manually enter a vendor bill. Leave project blank for overhead (office/marketing) expenses.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Header fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>
                Vendor <span className="text-destructive">*</span>
              </Label>
              <VendorSelectWithCreate value={vendorId} onValueChange={setVendorId} />
            </div>

            <div className="space-y-1.5">
              <Label>
                Project <span className="text-muted-foreground text-xs">(optional — leave blank for overhead)</span>
              </Label>
              <Select value={projectId || "__none__"} onValueChange={(v) => setProjectId(v === "__none__" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Overhead (no project)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Overhead (no project) —</SelectItem>
                  {(projects || []).map((p: any) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.project_number ? `${p.project_number} — ` : ""}{p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Bill # <span className="text-destructive">*</span></Label>
              <Input value={billNumber} onChange={(e) => setBillNumber(e.target.value)} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Bill Date <span className="text-destructive">*</span></Label>
                <Input type="date" value={billDate} onChange={(e) => setBillDate(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Due Date</Label>
                <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Payment Terms</Label>
              <Select value={terms} onValueChange={setTerms}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="due_on_receipt">Due on Receipt</SelectItem>
                  <SelectItem value="net_15">Net 15</SelectItem>
                  <SelectItem value="net_30">Net 30</SelectItem>
                  <SelectItem value="net_45">Net 45</SelectItem>
                  <SelectItem value="net_60">Net 60</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5 md:col-span-2">
              <Label>Memo / Description</Label>
              <Textarea
                rows={2}
                placeholder="Optional notes about this bill…"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>

          <Separator />

          {/* Line items */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Line Items</Label>
              <Button type="button" size="sm" variant="outline" onClick={addLine}>
                <Plus className="h-4 w-4 mr-1" /> Add Line
              </Button>
            </div>

            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[32%]">Description</TableHead>
                    <TableHead className="w-[22%]">
                      {hasProject ? "Cost Code / Account" : "Account (COA)"}
                    </TableHead>
                    <TableHead className="w-[9%] text-right">Qty</TableHead>
                    <TableHead className="w-[13%] text-right">Unit Price</TableHead>
                    <TableHead className="w-[14%] text-right">Total</TableHead>
                    <TableHead className="w-[40px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((l, i) => (
                    <TableRow key={l._key} className="align-top">
                      <TableCell>
                        <Textarea
                          rows={2}
                          value={l.description}
                          placeholder={`Line ${i + 1} description`}
                          onChange={(e) => updateLine(l._key, { description: e.target.value })}
                          className="min-h-[38px] text-sm"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1.5">
                          {hasProject && (
                            <Select
                              value={l.cost_code_id || "__none__"}
                              onValueChange={(v) => updateLine(l._key, { cost_code_id: v === "__none__" ? null : v })}
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue placeholder="Cost code…" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">— No cost code —</SelectItem>
                                {(costCodes || []).map((c: any) => (
                                  <SelectItem key={c.id} value={c.id} className="text-xs">
                                    {c.code} — {c.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          <AccountSelect
                            value={l.account_id ?? null}
                            onValueChange={(v) => updateLine(l._key, { account_id: v })}
                            placeholder={hasProject ? "Account (optional)…" : "Select account…"}
                            className="h-8 text-xs"
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number" step="0.01" min="0"
                          className="h-8 text-right text-sm"
                          value={l.quantity}
                          onChange={(e) => updateLine(l._key, { quantity: Number(e.target.value) || 0 })}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number" step="0.01" min="0"
                          className="h-8 text-right text-sm"
                          value={l.unit_price}
                          onChange={(e) => updateLine(l._key, { unit_price: Number(e.target.value) || 0 })}
                        />
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm pt-3">
                        {fmt(Number(l.line_total) || 0)}
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button" variant="ghost" size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() => removeLine(l._key)}
                          disabled={lines.length <= 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {!hasProject && (
              <p className="text-xs text-muted-foreground">
                Tip: use account <span className="font-mono">#34 Contract Labor</span> for people paid for work performed.
              </p>
            )}
          </div>

          {/* Attachments */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Attachments</Label>
            <div
              className="border-2 border-dashed rounded-md p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileRef.current?.click()}
            >
              <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-1" />
              <p className="text-xs text-muted-foreground">Click to attach receipts or supporting docs</p>
              <input
                ref={fileRef}
                type="file"
                multiple
                accept=".pdf,image/*"
                className="hidden"
                onChange={onFileSelect}
              />
            </div>
            {files.length > 0 && (
              <div className="space-y-1">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-muted/40 rounded px-2 py-1">
                    <span className="flex items-center gap-2 truncate">
                      <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="truncate">{f.name}</span>
                    </span>
                    <Button
                      type="button" variant="ghost" size="icon" className="h-5 w-5"
                      onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-64 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-mono">{fmt(subtotal)}</span>
              </div>
              <Separator />
              <div className="flex justify-between text-base font-semibold">
                <span>Total</span>
                <span className="font-mono">{fmt(subtotal)}</span>
              </div>
              {!hasProject && (
                <Badge variant="secondary" className="mt-2 text-xs">Overhead bill</Badge>
              )}
            </div>
          </div>

          {validationError && (
            <p className="text-xs text-destructive">{validationError}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={createBill.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {createBill.isPending ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</>
            ) : (
              <>Create Bill</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
